import { useState } from "react";
import { Ban, User as UserIcon } from "lucide-react";
import type { OrgUserSummary } from "@/lib/api";

export function userLabel(u: OrgUserSummary | null | undefined): string {
  if (!u) return "Unassigned";
  return u.displayName?.trim() || u.email;
}

export function userInitials(u: OrgUserSummary): string {
  const name = u.displayName?.trim();
  if (name) {
    const parts = name.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  return u.email.slice(0, 2).toUpperCase();
}

/**
 * Initials-in-a-circle avatar. `user = null` renders an empty dashed circle
 * for "unassigned" rows.
 */
export function Avatar({ user, size = 22 }: { user: OrgUserSummary | null; size?: number }) {
  if (!user) {
    return (
      <div
        className="rounded-full flex items-center justify-center border border-dashed flex-shrink-0"
        style={{
          width: size,
          height: size,
          borderColor: "rgba(255,255,255,0.15)",
          color: "#6B7280",
        }}
      >
        <UserIcon size={Math.round(size * 0.5)} />
      </div>
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center font-semibold flex-shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: "rgba(249,115,22,0.18)",
        color: "#FBA970",
        fontSize: size <= 22 ? 9 : 10,
      }}
    >
      {userInitials(user)}
    </div>
  );
}

/**
 * Searchable user picker. Used by the row owner-picker AND the bulk-action
 * Assign menu AND the side panel.
 */
export function UserPickerMenu({
  users,
  myId,
  onPick,
  allowUnassign = true,
}: {
  users: OrgUserSummary[];
  myId: string | null;
  onPick: (id: string | null) => void;
  allowUnassign?: boolean;
}) {
  const [filter, setFilter] = useState("");
  const filtered = users.filter((u) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (u.displayName ?? "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  // Float "me" to the top of the list for the common "assign to myself" path.
  const sorted = [...filtered].sort((a, b) => {
    if (a.id === myId) return -1;
    if (b.id === myId) return 1;
    return userLabel(a).localeCompare(userLabel(b));
  });

  return (
    <div
      className="absolute right-0 top-full mt-1 w-64 rounded-lg border shadow-xl py-2 z-20"
      style={{
        backgroundColor: "#1F2933",
        borderColor: "rgba(229,231,235,0.12)",
      }}
    >
      <div className="px-2 pb-2">
        <input
          type="text"
          autoFocus
          placeholder="Search teammates…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30"
        />
      </div>
      <div className="max-h-60 overflow-y-auto">
        {sorted.length === 0 && <p className="px-3 py-2 text-xs text-gray-500">No matches.</p>}
        {sorted.map((u) => (
          <button
            key={u.id}
            onClick={() => onPick(u.id)}
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/5 flex items-center gap-2"
          >
            <Avatar user={u} size={20} />
            <span className="truncate flex-1">
              {userLabel(u)}
              {u.id === myId && <span className="text-gray-500 ml-1">(you)</span>}
            </span>
          </button>
        ))}
      </div>
      {allowUnassign && (
        <>
          <div className="my-1 border-t mx-2" style={{ borderColor: "rgba(229,231,235,0.06)" }} />
          <button
            onClick={() => onPick(null)}
            className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-white/5 hover:text-white flex items-center gap-2"
          >
            <Ban size={12} />
            Unassign
          </button>
        </>
      )}
    </div>
  );
}
